/**
 * Map Cognito / Amplify error names to user-facing messages.
 *
 * Amplify v6 surfaces errors as plain `Error` objects whose `.name`
 * matches the Cognito exception (or an Amplify-internal name). We
 * pattern-match on that to render a friendly string in the form,
 * keeping any unhandled case as a generic message so users never see
 * a raw exception name.
 *
 * `UserNotConfirmedException` is intentionally absent: the pool's
 * PreSignUp Lambda auto-confirms every user, so we never expect
 * unconfirmed accounts. If Amplify ever raises it (e.g. legacy
 * accounts from a previous pool), we surface it as `Unknown` and log.
 */

export type KnownAuthError =
  | 'UserAlreadyExists'
  | 'NotAuthorized'
  | 'UserNotFound'
  | 'LimitExceeded'
  | 'InvalidPassword'
  | 'InvalidParameter'
  | 'Network'
  | 'Unknown';

export interface MappedAuthError {
  code: KnownAuthError;
  message: string;
}

const MESSAGES: Record<KnownAuthError, string> = {
  UserAlreadyExists: 'That email is already registered. Try logging in.',
  NotAuthorized: 'Wrong email or password.',
  UserNotFound: "We don't have an account for that email. Create one in a few seconds.",
  LimitExceeded: 'Too many attempts. Wait a minute and try again.',
  InvalidPassword:
    'Password needs 8+ characters with an uppercase, a lowercase, a number, and a symbol.',
  InvalidParameter: 'Some of those details look wrong. Check the highlighted fields.',
  Network: 'Network blip. Try again.',
  Unknown: 'Something went wrong. Try again or contact support.',
};

export function mapAuthError(error: unknown): MappedAuthError {
  if (!(error instanceof Error)) {
    return { code: 'Unknown', message: MESSAGES.Unknown };
  }

  const name = error.name;
  const lookup: Record<string, KnownAuthError> = {
    UsernameExistsException: 'UserAlreadyExists',
    NotAuthorizedException: 'NotAuthorized',
    UserNotFoundException: 'UserNotFound',
    LimitExceededException: 'LimitExceeded',
    InvalidPasswordException: 'InvalidPassword',
    InvalidParameterException: 'InvalidParameter',
    NetworkError: 'Network',
  };

  const code = lookup[name] ?? 'Unknown';
  return { code, message: MESSAGES[code] };
}
